import React, { useState, useEffect, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader, Clock, ArrowUp, ArrowDown, Eye } from 'lucide-react';
import { televisaoService, Televisao } from '../services/televisaoService';
import { bannerService, TelaBanner, TelaBannerComProcessamentoSeletivo, TelaBannerParaTelevisao, TelaBannerBasica } from '../../banners/services/bannerService';
import { TIPO_TO_LAYOUT } from '../../banners/services/bannerSyncService';
import { TelaParaTelevisao } from '../../banners/services/bannerService';

interface LinkScreensModalProps {
    televisao: Televisao | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (televisaoId: number, telaIds: number[], temposTelas: Map<number, number>, ordensTelas: Map<number, number>, diasTelas: Map<number, string[]>) => Promise<void>;
}

interface TelaComConfig {
    tela: TelaParaTelevisao; // Usar tipo otimizado para televisão (id, nome, tipo)
    tempo: number;
    ordem: number;
    isVideo: boolean;
    dias?: string[];
}

// Dias da semana: Domingo=0, Segunda=1, Terça=2, Quarta=3, Quinta=4, Sexta=5, Sábado=6
const DIAS_SEMANA = [
    { label: 'D', value: '0', nome: 'Domingo' },
    { label: 'S', value: '1', nome: 'Segunda' },
    { label: 'T', value: '2', nome: 'Terça' },
    { label: 'Q', value: '3', nome: 'Quarta' },
    { label: 'Q', value: '4', nome: 'Quinta' },
    { label: 'S', value: '5', nome: 'Sexta' },
    { label: 'S', value: '6', nome: 'Sábado' }
];

// Função para obter nome amigável do layout
const getLayoutDisplayName = (layoutId: string): string => {
    const layoutNames: Record<string, string> = {
        'simple2col': 'Lista com Colunas',
        'panfleto': 'Panfleto',
        'video': 'Vídeo'
    };
    return layoutNames[layoutId] || layoutId;
};

export const LinkScreensModal = memo(({
    televisao,
    isOpen,
    onClose,
    onSave
}: LinkScreensModalProps) => {
    const navigate = useNavigate();
    const [telasDisponiveis, setTelasDisponiveis] = useState<TelaParaTelevisao[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedTelaIds, setSelectedTelaIds] = useState<number[]>([]);
    const [temposTelas, setTemposTelas] = useState<Map<number, number>>(new Map());
    const [ordensTelas, setOrdensTelas] = useState<Map<number, number>>(new Map());
    const [temposInput, setTemposInput] = useState<Map<number, string>>(new Map());
    const [diasTelas, setDiasTelas] = useState<Map<number, string[]>>(new Map()); // Map<telaId, dias[]>

    useEffect(() => {
        if (isOpen && televisao) {
            loadTelas();
            // Carregar telas já vinculadas
            const telaIds = televisaoService.stringToTelaIds(televisao.dsTelasCadastradas);
            setSelectedTelaIds(telaIds);
            
            // Carregar configurações (tempo, ordem e dias) já configuradas
            const configs = (televisaoService as any).extrairTodasConfigs?.(televisao.dsConfiguracoes) || new Map();
            const temposMap = new Map<number, number>();
            const ordensMap = new Map<number, number>();
            const diasMap = new Map<number, string[]>();
            
            configs.forEach((config: { tempo: number; ordem: number; dias?: string[] }, telaId: number) => {
                if (config) {
                    if (config.tempo > 0) {
                        temposMap.set(telaId, config.tempo);
                    }
                    if (config.ordem > 0) {
                        ordensMap.set(telaId, config.ordem);
                    }
                    // IMPORTANTE: Carregar dias mesmo se não tiver tempo ou ordem
                    if (config.dias && Array.isArray(config.dias) && config.dias.length > 0) {
                        diasMap.set(telaId, config.dias);
                    }
                }
            });
            
            setTemposTelas(temposMap);
            setOrdensTelas(ordensMap);
            setDiasTelas(diasMap);
            
            // Inicializar valores dos inputs
            setTemposInput(new Map(Array.from(temposMap.entries()).map(([id, tempo]) => [id, tempo.toString()])));
        }
    }, [isOpen, televisao]);

    const loadTelas = async () => {
        setLoading(true);
        try {
            // OTIMIZAÇÃO CRÍTICA: Usar método otimizado para televisão
            // Retorna APENAS: id, nome, tipo
            // NÃO retorna: descricao, produtos, configuracoes gigante, etc.
            // O resto (tempo, agenda, etc.) é da TV, não da tela
            const telas = await bannerService.listarTelasOtimizadoParaTelevisao();
            setTelasDisponiveis(telas);
        } catch (error) {
            console.error('Erro ao carregar telas:', error);
            alert('Erro ao carregar telas disponíveis');
        } finally {
            setLoading(false);
        }
    };

    // Criar lista de telas selecionadas com suas configurações, ordenadas por ordem
    const telasSelecionadasOrdenadas = useMemo(() => {
        const telasComConfig: TelaComConfig[] = [];
        
        selectedTelaIds.forEach((telaId, index) => {
            const tela = telasDisponiveis.find(t => t.id === telaId);
            if (!tela || tela.id == null) return;
            
            const layoutId = tela.tipo !== undefined
                ? (TIPO_TO_LAYOUT[tela.tipo] || 'simple2col')
                : 'simple2col';
            const isVideo = layoutId === 'video';
            
            const tempo = temposTelas.get(telaId) || (isVideo ? 0 : 15);
            // IMPORTANTE: Ordem é obrigatória - se não tiver, usar índice + 1
            const ordem = ordensTelas.get(telaId) || (index + 1);
            
            const dias = diasTelas.get(telaId) || [];
            
            telasComConfig.push({
                tela,
                tempo,
                ordem,
                isVideo,
                dias
            });
        });
        
        // Ordenar por ordem (sempre tem ordem agora)
        telasComConfig.sort((a, b) => a.ordem - b.ordem);
        
        return telasComConfig;
    }, [selectedTelaIds, telasDisponiveis, temposTelas, ordensTelas, diasTelas]);

    const handleToggleTela = (telaId: number) => {
        setSelectedTelaIds(prev => {
            if (prev.includes(telaId)) {
                // Remover tela e suas configurações
                setTemposTelas(prevTempos => {
                    const newTempos = new Map(prevTempos);
                    newTempos.delete(telaId);
                    return newTempos;
                });
                setOrdensTelas(prevOrdens => {
                    const newOrdens = new Map(prevOrdens);
                    newOrdens.delete(telaId);
                    return newOrdens;
                });
                setTemposInput(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(telaId);
                    return newMap;
                });
                setDiasTelas(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(telaId);
                    return newMap;
                });
                return prev.filter(id => id !== telaId);
            } else {
                // Adicionar tela e definir valores padrão
                const tela = telasDisponiveis.find(t => t.id === telaId);
                const layoutId = tela?.tipo !== undefined
                    ? (TIPO_TO_LAYOUT[tela.tipo] || 'simple2col')
                    : 'simple2col';
                const isVideo = layoutId === 'video';
                
                // Se não for vídeo, definir tempo padrão de 15 segundos
                if (!isVideo) {
                    setTemposTelas(prevTempos => {
                        const newTempos = new Map(prevTempos);
                        newTempos.set(telaId, 15);
                        return newTempos;
                    });
                    setTemposInput(prevInput => {
                        const newInput = new Map(prevInput);
                        newInput.set(telaId, '15');
                        return newInput;
                    });
                }
                
                // IMPORTANTE: Definir ordem padrão obrigatória (última posição)
                const novaOrdem = prev.length + 1;
                setOrdensTelas(prevOrdens => {
                    const newOrdens = new Map(prevOrdens);
                    newOrdens.set(telaId, novaOrdem);
                    return newOrdens;
                });
                
                return [...prev, telaId];
            }
        });
    };

    const handleTempoChange = (telaId: number, tempo: string) => {
        setTemposInput(prev => {
            const newMap = new Map(prev);
            newMap.set(telaId, tempo);
            return newMap;
        });
        
        if (tempo.trim() !== '') {
            const tempoNum = parseInt(tempo, 10);
            if (!isNaN(tempoNum) && tempoNum > 0) {
                setTemposTelas(prev => {
                    const newMap = new Map(prev);
                    newMap.set(telaId, tempoNum);
                    return newMap;
                });
            }
        }
    };

    const handleTempoBlur = (telaId: number, tempo: string) => {
        let tempoFinal: number;
        if (tempo.trim() === '') {
            tempoFinal = 1;
        } else {
            const tempoNum = parseInt(tempo, 10);
            tempoFinal = (isNaN(tempoNum) || tempoNum < 1) ? 1 : tempoNum;
        }
        
        setTemposTelas(prev => {
            const newMap = new Map(prev);
            newMap.set(telaId, tempoFinal);
            return newMap;
        });
        setTemposInput(prev => {
            const newMap = new Map(prev);
            newMap.set(telaId, tempoFinal.toString());
            return newMap;
        });
    };


    const handleToggleDia = (telaId: number, diaValue: string) => {
        setDiasTelas(prev => {
            const newMap = new Map(prev);
            const diasAtuais = newMap.get(telaId) || [];
            
            if (diasAtuais.includes(diaValue)) {
                // Remover dia
                newMap.set(telaId, diasAtuais.filter(d => d !== diaValue));
            } else {
                // Adicionar dia
                newMap.set(telaId, [...diasAtuais, diaValue]);
            }
            
            return newMap;
        });
    };


    const handleMoveUp = (telaId: number) => {
        const currentIndex = telasSelecionadasOrdenadas.findIndex(item => item.tela.id === telaId);
        if (currentIndex <= 0) return; // Já está no topo

        const reordered = [...telasSelecionadasOrdenadas];
        const [removed] = reordered.splice(currentIndex, 1);
        reordered.splice(currentIndex - 1, 0, removed);

        // Atualizar ordens baseado na nova posição (sequencial: 1, 2, 3, 4, 5...)
        const novasOrdens = new Map<number, number>();
        
        reordered.forEach((item, index) => {
            const id = item.tela.id;
            if (!id) return;
            const novaOrdem = index + 1;
            novasOrdens.set(id, novaOrdem);
        });

        setOrdensTelas(novasOrdens);
    };

    const handleMoveDown = (telaId: number) => {
        const currentIndex = telasSelecionadasOrdenadas.findIndex(item => item.tela.id === telaId);
        if (currentIndex < 0 || currentIndex >= telasSelecionadasOrdenadas.length - 1) return; // Já está no final

        const reordered = [...telasSelecionadasOrdenadas];
        const [removed] = reordered.splice(currentIndex, 1);
        reordered.splice(currentIndex + 1, 0, removed);

        // Atualizar ordens baseado na nova posição (sequencial: 1, 2, 3, 4, 5...)
        const novasOrdens = new Map<number, number>();
        
        reordered.forEach((item, index) => {
            const id = item.tela.id;
            if (!id) return;
            const novaOrdem = index + 1;
            novasOrdens.set(id, novaOrdem);
        });

        setOrdensTelas(novasOrdens);
    };

    const handleSave = async () => {
        if (!televisao || televisao.cdTelevisao == null) {
            alert('Erro: ID da televisão inválido');
            return;
        }

        // Garantir que todas as telas selecionadas (exceto vídeos) tenham tempo configurado
        const temposCompletos = new Map(temposTelas);
        selectedTelaIds.forEach(telaId => {
            const tela = telasDisponiveis.find(t => t.id === telaId);
            const layoutId = tela?.tipo !== undefined
                ? (TIPO_TO_LAYOUT[tela.tipo] || 'simple2col')
                : 'simple2col';
            const isVideo = layoutId === 'video';
            
            if (!isVideo && !temposCompletos.has(telaId)) {
                temposCompletos.set(telaId, 15);
            }
        });

        // IMPORTANTE: Garantir que todas as telas tenham ordem configurada (obrigatório)
        const ordensCompletas = new Map(ordensTelas);
        // Primeiro, ordenar as telas pela ordem atual para garantir ordem correta
        const telasComOrdem = selectedTelaIds.map(telaId => ({
            id: telaId,
            ordem: ordensCompletas.get(telaId) || 9999 // Usar 9999 para telas sem ordem (vão para o final)
        }));
        telasComOrdem.sort((a, b) => a.ordem - b.ordem);
        
        // Atribuir ordem sequencial obrigatória (1, 2, 3, ...)
        telasComOrdem.forEach((item, index) => {
            ordensCompletas.set(item.id, index + 1);
        });

        setSaving(true);
        try {
            await onSave(televisao.cdTelevisao, selectedTelaIds, temposCompletos, ordensCompletas, diasTelas);
            onClose();
        } catch (error) {
            console.error('Erro ao salvar:', error);
            alert('Erro ao salvar vinculação de telas');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !televisao) return null;

    // Telas não selecionadas (para mostrar na lista de disponíveis)
    const telasNaoSelecionadas = telasDisponiveis.filter(tela => 
        tela.id != null && !selectedTelaIds.includes(tela.id)
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-800">
                        Vincular Telas - {televisao.nmTelevisao}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader className="animate-spin text-blue-600" size={32} />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Telas Selecionadas (com ordem) */}
                            {telasSelecionadasOrdenadas.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Telas Selecionadas (Ordenadas)</h3>
                                    <div className="space-y-2">
                                        {telasSelecionadasOrdenadas.map((item, index) => {
                                            const tela = item.tela;
                                            if (!tela.id) return null; // Skip se não tiver ID
                                            
                                            const tempoInput = temposInput.get(tela.id) || (item.tempo || '');
                                            const isFirst = index === 0;
                                            const isLast = index === telasSelecionadasOrdenadas.length - 1;

                                            return (
                                                <div
                                                    key={tela.id}
                                                    className="p-3 border-2 rounded-lg bg-blue-50 border-blue-300"
                                                >
                                                    <div className="flex items-start gap-2">
                                                        {/* Botões de ordenação */}
                                                        {tela.id && (
                                                            <div className="flex flex-col gap-1 mt-1">
                                                                <button
                                                                    onClick={() => handleMoveUp(tela.id!)}
                                                                    disabled={isFirst}
                                                                    className={`p-1 rounded ${
                                                                        isFirst 
                                                                            ? 'text-gray-300 cursor-not-allowed' 
                                                                            : 'text-gray-600 hover:text-blue-600 hover:bg-blue-100'
                                                                    }`}
                                                                    title="Mover para cima"
                                                                >
                                                                    <ArrowUp size={16} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleMoveDown(tela.id!)}
                                                                    disabled={isLast}
                                                                    className={`p-1 rounded ${
                                                                        isLast 
                                                                            ? 'text-gray-300 cursor-not-allowed' 
                                                                            : 'text-gray-600 hover:text-blue-600 hover:bg-blue-100'
                                                                    }`}
                                                                    title="Mover para baixo"
                                                                >
                                                                    <ArrowDown size={16} />
                                                                </button>
                                                            </div>
                                                        )}

                                                        <div className="flex-1">
                                                            {/* Título e botão na mesma linha */}
                                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                                <div className="font-medium text-gray-800">{tela.nome || 'Sem nome'}</div>
                                                                {tela.id && (
                                                                    <button
                                                                        onClick={() => navigate(`/display/${tela.id}?settings=true`)}
                                                                        className="flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors font-medium whitespace-nowrap"
                                                                        title="Personalizar visualização da tela"
                                                                    >
                                                                        <Eye size={14} />
                                                                        Personalizar visualização da tela
                                                                    </button>
                                                                )}
                                                            </div>
                                                                                {/* Descrição removida - não é necessária para televisão */}
                                                                                <div className="text-xs text-gray-400 mt-1">
                                                                                    <span className="font-semibold text-blue-600">ID:</span> {tela.id} | <span className="font-semibold text-blue-600">Tipo:</span> {getLayoutDisplayName(tela.tipo !== undefined ? (TIPO_TO_LAYOUT[tela.tipo] || 'simple2col') : 'simple2col')}
                                                                                </div>

                                                                                <div className="mt-3 flex items-center gap-4 flex-wrap">
                                                                                    {/* Exibir Ordem */}
                                                                                    <div className="flex items-center gap-2">
                                                                                        <label className="text-sm text-gray-700 font-semibold">Ordem:</label>
                                                                                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-semibold">
                                                                                            {item.ordem}
                                                                                        </span>
                                                                                    </div>

                                                                                    {/* Campo de Tempo - apenas para não-vídeo */}
                                                                                    {!item.isVideo && tela.id && (
                                                                                        <div className="flex items-center gap-2">
                                                                                            <Clock size={16} className="text-gray-500" />
                                                                                            <label className="text-sm text-gray-700">Tempo (seg):</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                min="1"
                                                                                                step="1"
                                                                                                value={tempoInput}
                                                                                                onChange={(e) => handleTempoChange(tela.id!, e.target.value)}
                                                                                                onBlur={(e) => handleTempoBlur(tela.id!, e.target.value)}
                                                                                                placeholder="Ex: 15"
                                                                                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                                                                            />
                                                                                        </div>
                                                                                    )}

                                                                                    {item.isVideo && (
                                                                                        <div className="flex items-center gap-2">
                                                                                            <Clock size={16} className="text-gray-500" />
                                                                                            <label className="text-sm text-gray-700">Tempo (seg):</label>
                                                                                            <div className="w-24 px-2 py-1 border border-gray-300 rounded text-sm bg-gray-100 text-gray-500 cursor-default select-none">
                                                                                                Automático
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </div>

                                                                                {/* Seleção de Dias da Semana */}
                                                                                {tela.id && (
                                                                                    <div className="mt-3">
                                                                                        <label className="text-xs text-gray-600 mb-2 block">
                                                                                            Selecione os dias que essa tela irá aparecer:
                                                                                        </label>
                                                                                        <div className="flex items-center gap-2">
                                                                                            {DIAS_SEMANA.map((dia) => {
                                                                                                const diasTela = diasTelas.get(tela.id!) || [];
                                                                                                const isSelected = diasTela.includes(dia.value);
                                                                                                return (
                                                                                                    <button
                                                                                                        key={dia.value}
                                                                                                        type="button"
                                                                                                        onClick={() => handleToggleDia(tela.id!, dia.value)}
                                                                                                        className={`w-8 h-8 rounded border-2 flex items-center justify-center text-sm font-semibold transition-colors ${
                                                                                                            isSelected
                                                                                                                ? 'border-blue-600 text-blue-600 bg-blue-50'
                                                                                                                : 'border-gray-300 text-gray-500 bg-white hover:border-gray-400'
                                                                                                        }`}
                                                                                                        title={dia.nome}
                                                                                                    >
                                                                                                        {dia.label}
                                                                                                    </button>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                        <button
                                                            onClick={() => tela.id && handleToggleTela(tela.id)}
                                                            className="text-red-500 hover:text-red-600"
                                                            title="Remover tela"
                                                        >
                                                            <X size={20} />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Telas Disponíveis (não selecionadas) */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-700 mb-4">Telas Disponíveis</h3>
                                {telasNaoSelecionadas.length === 0 ? (
                                    <p className="text-gray-500 text-center py-4">Todas as telas já estão selecionadas</p>
                                ) : (
                                    <div className="space-y-2">
                                        {telasNaoSelecionadas
                                            .filter(tela => tela.id != null)
                                            .map(tela => {
                                                const layoutId = tela.tipo !== undefined
                                                    ? (TIPO_TO_LAYOUT[tela.tipo] || 'simple2col')
                                                    : 'simple2col';
                                                const isVideo = layoutId === 'video';
                                                
                                                // Para televisão, só precisamos: id, nome, tipo
                                                // Descrição e categoria não são necessárias

                                                return (
                                                    <label
                                                        key={tela.id!}
                                                        className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={false}
                                                            onChange={() => tela.id && handleToggleTela(tela.id)}
                                                            className="mr-3"
                                                        />
                                                        <div className="flex-1">
                                                            <div className="font-medium text-gray-800">{tela.nome || 'Sem nome'}</div>
                                                            <div className="text-xs text-gray-400 mt-1">
                                                                <span className="font-semibold text-blue-600">ID:</span> {tela.id} | <span className="font-semibold text-blue-600">Tipo:</span> {getLayoutDisplayName(layoutId)}
                                                                {/* Descrição e categoria removidas - não são necessárias para televisão */}
                                                            </div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {saving && <Loader className="animate-spin" size={16} />}
                        Salvar
                    </button>
                </div>
            </div>
        </div>
    );
});

LinkScreensModal.displayName = 'LinkScreensModal';
